/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { useEffect } from 'react';

import Imgix from 'js/components/Imgix';

import { Button, ChipGroup, Grid } from '@coursera/cds-core';
import { AddIcon } from '@coursera/cds-icons';

import OptionPill from 'bundles/onboarding-common/components/OptionPill';
import { QUESTION_IDS } from 'bundles/onboarding-common/constants/questionScheme';
import { useOnboardingQuestionData } from 'bundles/onboarding-common/contexts/OnboardingQuestionDataContext';
import useArtificiallyPaginatedRoles from 'bundles/onboarding-common/hooks/useArtificiallyPaginatedRoles';
import useGetDefaultSavedRoles from 'bundles/onboarding-common/hooks/useGetDefaultSavedRoles';
import useRoleSelectQuestionState from 'bundles/onboarding-common/hooks/useRoleSelectQuestionState';
import type { RoleFragment } from 'bundles/onboarding-common/queries/__generated__/RoleFragment';
import type { RoleWithState } from 'bundles/onboarding-common/types';

import _t from 'i18n!nls/onboarding-common';

const RoleSelectQuestion = ({ defaultValue, roles }: { defaultValue: RoleWithState[]; roles: RoleFragment[] }) => {
  const {
    containerRef,
    rolesPerRow,
    iDontKnowRole,
    handleClick,
    onViewMoreClick,
    onViewAllClick,
    isLoading,
    isExpanded,
  } = useRoleSelectQuestionState({ defaultValue, roles });

  return (
    <Grid item xs={12} ref={containerRef}>
      {
        // TODO: Add a loading state
        isLoading ? null : (
          <ChipGroup multiSelect={true} aria-label={_t('Select your role(s)')}>
            {rolesPerRow?.map((rolesRow, index) => (
              <div
                key={rolesRow[0]?.id}
                css={css`
                  display: flex;
                  flex-wrap: nowrap;
                  overflow: hidden;
                  gap: var(--cds-spacing-100);
                `}
              >
                {rolesRow?.map((role) => {
                  if (role.id === iDontKnowRole?.id) {
                    return (
                      <OptionPill
                        key={role.id}
                        label={role.name || ''}
                        onClick={() => handleClick(role)}
                        selected={role.isSelected}
                        variant="textOnly"
                        spacing="small"
                      />
                    );
                  }

                  return (
                    <OptionPill
                      key={role.id}
                      label={role.name || ''}
                      icon={<Imgix src={role.onboardingRoleOptionImage?.url || ''} width={16} height={16} alt="" />}
                      onClick={() => handleClick(role)}
                      selected={role.isSelected}
                      variant="withImageIcon"
                    />
                  );
                })}
                {index === rolesPerRow.length - 1 && (
                  <Button
                    variant="ghost"
                    size="small"
                    icon={<AddIcon size="medium" />}
                    iconPosition="before"
                    onClick={isExpanded ? onViewAllClick : onViewMoreClick}
                  >
                    {isExpanded ? _t('View all') : _t('View more')}
                  </Button>
                )}
              </div>
            ))}
          </ChipGroup>
        )
      }
    </Grid>
  );
};

const RoleSelectQuestionWrapper = () => {
  const { setQuestionValue } = useOnboardingQuestionData();
  const { defaultValue, isLoadingDefaultRoles } = useGetDefaultSavedRoles();
  const { roles, loading: isLoadingRoles } = useArtificiallyPaginatedRoles({ skip: 0, limit: 20 });

  useEffect(() => {
    if (defaultValue) {
      setQuestionValue({
        __typename: QUESTION_IDS.roleSelect,
        value: defaultValue.map((role) => ({
          id: role.id,
          name: role.name || '',
          type: role.type,
        })),
      });
    }
  }, [defaultValue, setQuestionValue]);

  if (isLoadingDefaultRoles || isLoadingRoles || !roles || !defaultValue) {
    // TODO: Add a loading state
    return null;
  }

  return <RoleSelectQuestion defaultValue={defaultValue} roles={roles} />;
};

export default RoleSelectQuestionWrapper;
